import type { ArticuloKit, InfoArticulo, KitComponente, } from "@/hooks/Api/KitsFabricacion";
import { BaseModal, ModalCloseCross } from "../../../../components/modals/BaseModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { TypedTable } from "@/components/TypedTable";
import { z } from "zod";
import { notificacionTipo } from "@/components/Alerts";
import { ComunicacionAjax } from "@/ts/api/comunicacion";
import { useQueryClient } from "@tanstack/react-query";
import { getArticuloByReference, refreshArticuloConKit, } from "@/hooks/Api/KitsFabricacion";
import { FormComponente } from "@/components/forms/FormAddComponenteKit";

interface ModalEditarKitProps {
	readonly shown: boolean;
	readonly close: () => void;
	readonly articuloConKit: ArticuloKit | null;
}
export function ModalEditarKit(props: ModalEditarKitProps) {
	const queryClient = useQueryClient();
	const { articuloConKit, close } = props;
	const [componentes, setComponentes] = useState<KitComponente[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (props.articuloConKit) {
			const componentes = props.articuloConKit.componentes ?? [];
			setComponentes(componentes);
		}
	}, [props.articuloConKit]);

	const removeComponent = useCallback((idComponente: number) => {
		setComponentes(prev => prev.filter(comp => comp.idComponente != idComponente));
	}, []);

	const addComponent = useCallback((articulo: InfoArticulo, cantidadPorUnidad: number, cantidadDivisor: number) => {
		setComponentes(prev => [...prev, {
			idComponente: articulo.id,
			refComponente: articulo.referencia,
			descComponente: articulo.descripcion,
			cantidadPorUnidad,
			cantidadDivisor,
		}]);
	}, []);

	const onGuardar = useCallback(async () => {
		if (articuloConKit?.idArticulo) {
			setIsLoading(true);
			const result = await updateKits(articuloConKit.idArticulo, componentes);
			if (result.status === "error") {
				notificacionTipo("error", result.message ?? "Error desconocido al guardar el kit.");
			} else {
				await refreshArticuloConKit(articuloConKit.idArticulo, queryClient);
				notificacionTipo("success", result.message ?? "Kit guardado correctamente.");
				close();
			}
			setIsLoading(false);
		}
	}, [componentes, close, articuloConKit, queryClient]);

	return (
		<BaseModal show={props.shown} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Kit</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body" style={{ marginBottom: 20 }}>
				<h2 style={{ margin: 0 }}>
					<span>Conjunto: </span>
					<span style={{ color: "#666666", fontWeight: "bold" }} >
						{props.articuloConKit?.descArticulo} ({props.articuloConKit?.referencia})
					</span>
				</h2>

				<div style={{ marginTop: 35 }}>
					<div className="table-responsive" style={{ marginTop: 20, borderBottom: "1px solid #111", display: "contents" }}>
						<span className="labelsForm" style={{ width: "100%" }} >Componentes</span>
						<TablaComponentes data={componentes} removeComponent={removeComponent} />
						<FormComponente nombreConjunto={props.articuloConKit?.descArticulo ?? ""} componentes={componentes} addComponente={addComponent} />
					</div>
				</div>
			</div>

			<div className="modal-footer" style={{ display: "block" }}>
				<button type="button" className="btnSecondary" style={{ float: "left" }} onClick={props.close} disabled={isLoading}>Cerrar</button>
				<button type="button" className="btnPrimary" onClick={() => void onGuardar()} disabled={isLoading}>Guardar</button>
			</div>
		</BaseModal>
	);
}

function TablaComponentes(props: { readonly data: KitComponente[]; readonly removeComponent: (idComponente: number) => void; }) {
	const { removeComponent } = props;
	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<KitComponente>();

		const getBtnEliminar = (idComponente: number) => {
			return (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<button className="eliminarComponente" type="button" onClick={() => removeComponent(idComponente)}>
						<i className="fa fa-trash" />
					</button>
				</div>
			);
		};


		return [
			columnHelper.accessor("refComponente", { enableSorting: false, header: "Referencia", meta: { className: "center" }, cell: info => info.getValue() }),
			columnHelper.accessor("descComponente", { enableSorting: false, header: "Descripción", meta: { className: "center" } }),
			columnHelper.accessor("cantidadPorUnidad", { enableSorting: false, header: "Cantidad por ", meta: { className: "center" }, }),
			columnHelper.accessor("cantidadDivisor", { enableSorting: false, header: "n unidades de conjunto", meta: { className: "center" }, }),
			columnHelper.display({
				id: "eliminar",
				header: "",
				meta: { className: "center" },
				cell: info => getBtnEliminar(info.row.original.idComponente),
			}),
		];
	}, [removeComponent]);

	return (
		<TypedTable
			// scrollY="400px"
			className="dataTable display"
			columns={columns}
			data={props.data}
		/>
	);
}





function updateKits(idConjunto: number, componentes: KitComponente[]): Promise<ErrorGuardar> {
	const parametro = {
		"idConjunto": idConjunto,
		"componentes": componentes,
	};


	const url = "/api/v1/kitArticulo";
	return ComunicacionAjax.sendAjaxRequest<ErrorGuardar>("POST", url, parametro);
}
type ErrorGuardar = {
	message: string;
	status: "error";
} | {
	status: unknown;
	message?: string;
};


export const zInputsAddComponente = z.object({
	referencia: z.string(),					// Optional porque no se muestra en OF
	cantidadPorUnidad: z.number().min(Number.EPSILON, { message: "La cantidad debe ser mayor que 0" }).nullable(),
	cantidadDivisor: z.int().min(1).nullable(),
});
export type iInputsAddComponente = z.infer<typeof zInputsAddComponente>;